import { isSoldOutPlaceholder } from '@app/model/company_hotel_room_mappings_type_configuration/isSoldOutPlaceholder';
import React, { useState } from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import _isEmpty from 'lodash/isEmpty';

import _capitalize from 'lodash/capitalize';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import DataProvider from '@app/model/_common/DataProvider';

import { DndContext, DragOverlay, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import MappingTypeDropArea from '@app/site/secured/rateshopper/rates/settings/page/room-mapping/fragment/MappingTypeDropArea';
import HotelRoomWithMapping from '@app/model/company_hotel_room_mappings_type_configuration/HotelRoomWithMapping';
import RoomTag from '@app/site/secured/rateshopper/rates/settings/page/room-mapping/fragment/RoomTag';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import {
	useUpdateCompanyHotelRoomMappingTypeMutation,
} from '@app/model/company_hotel/service/CompanyHotelServiceHooks';
import HotelWithRoomMappings from '@app/model/company_hotel/HotelWithRoomMappings';

const CompanyHotelRoomMappingItemFragment: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	hotelWithRoomMappings: HotelWithRoomMappings,
	provider: DataProvider
}> = ({ companyHotel, hotelWithRoomMappings, provider }) => {

	const toaster = useToaster();
	const { t } = useTranslation();

	const updateRoomMappingMutation = useUpdateCompanyHotelRoomMappingTypeMutation({
		onError: (e: string) => toaster.error('Room Mappings', t(e)),
	});


	const changeRoomType = (roomId: number, roomMappingType: HotelRoomMappingType) => {
		updateRoomMappingMutation.mutate({
			companyHotelId: companyHotel.id,
			hotelId: hotelWithRoomMappings.hotel.id,
			roomId: roomId,
			type: roomMappingType,
		});
	};
	
	const [draggedItem, setDraggedItem] = useState<HotelRoomWithMapping | undefined>(undefined);

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const handleDragStart = (event: any) => {
		const item = event.active.data.current;
		setDraggedItem(item.roomMapping);
	};

	const handleDragOver = (event: any) => {
		const item = event.active.data.current;
		if (!!event.over) {
			const type = event.over.id;
			changeRoomType(item.roomMapping.roomId, type);
			setDraggedItem(undefined);
		}
	};

	const roomMappings = hotelWithRoomMappings.roomMappings.filter(rm => rm.provider === provider
		&& rm.roomIsActive && !rm.roomIsPartnerOffer && !isSoldOutPlaceholder(rm));

	return <div
		className="divide-y divide-gray-200 rounded-lg bg-gray-50 ring-1 ring-gray-800/10 shadow">
		{/* Header */}
		<div className="px-4 py-5 sm:px-6">
			<div className="flex flex-wrap items-center justify-between sm:flex-nowrap min-h-[38px]">
				<div className="flex items-center">
					<h3 className="text-lg font-semibold leading-6 text-gray-900">
						{hotelWithRoomMappings.hotel.name}
					</h3>
					{provider === DataProvider.BOOKING &&
						<a href={hotelWithRoomMappings.hotel.url} className="ml-1 font-semibold text-blue-600 hover:text-blue-500"
							 target="_blank">
							<ArrowTopRightOnSquareIcon className="h-4 w-4" />
						</a>}
				</div>
				<div className="flex-shrink-0">
					{_isEmpty(roomMappings) &&
						<button type="button"
										className="btn btn-disabled pointer-events-none bg-red-50 text-red-400">No
							Rooms Found</button>}
				</div>
			</div>
		</div>
		{/* Body */}
		{!_isEmpty(roomMappings) && <DndContext onDragEnd={handleDragOver}
																																	onDragStart={handleDragStart}
																																	autoScroll={false}
																																	sensors={sensors}>
			<DragOverlay modifiers={[restrictToWindowEdges]}>{draggedItem ?
				<RoomTag roomMapping={draggedItem} isOverlay /> : null}</DragOverlay>

			<div className="px-4 py-5 sm:p-6">
				<div className="mb-6 py-3 px-4 rounded-md bg-white ring-1 ring-gray-800/10">
					<div className="text-left">
						<h3 className="text-sm font-semibold leading-6 text-gray-900">
							Hotel Rooms
						</h3>
					</div>
					<div className="flex flex-wrap space-y-3 space-x-4 text-gray-800">
						<MappingTypeDropArea hotel={hotelWithRoomMappings.hotel}
																 roomMappings={roomMappings}
																 isHorizontal
																 type={HotelRoomMappingType.NONE} />
					</div>
				</div>
				{!_isEmpty(companyHotel.roomMappingTypeConfigurations) && <div
					className={`grid grid-cols-1 items-start gap-x-6 gap-y-10 sm:grid-cols-5`}>
					{companyHotel.orderedRoomMappingTypeConfigurations()!.filter(c => c.isEnabled && c.type !== HotelRoomMappingType.NONE && (companyHotel.settings.roomMapping.showSystemRoomMappingTypes || !c.isSystem))
						.map(mt => <div key={mt.type} className="col-span-1 h-full">
							<div className="py-3 px-4 rounded-md bg-white ring-1 ring-gray-800/10 min-h-full">
								<div className="text-left">
									<h3 className="text-sm font-semibold leading-6 text-gray-900">{_capitalize(mt.name)}</h3>
								</div>
								<MappingTypeDropArea hotel={hotelWithRoomMappings.hotel} roomMappings={roomMappings} type={mt.type} />
							</div>
						</div>)}
				</div>}
			</div>
		</DndContext>}

	</div>;
};

export default CompanyHotelRoomMappingItemFragment;